import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X } from "lucide-react";
import clsx from "clsx";
import { useDialog } from "../../stores/dialogStore";

export function DialogHost() {
  const current = useDialog((s) => s.current);
  const resolve = useDialog((s) => s._resolve);

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          key="dialog-overlay"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
        >
          <div
            className="absolute inset-0 bg-black/65 backdrop-blur-sm"
            onClick={() => resolve(current.kind === "prompt" ? null : false)}
          />
          {current.kind === "prompt" ? (
            <PromptBody key={`p-${Date.now()}`} opts={current.opts} onResolve={resolve} />
          ) : (
            <ConfirmBody opts={current.opts} onResolve={resolve} />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function PromptBody({
  opts,
  onResolve,
}: {
  opts: import("../../stores/dialogStore").PromptOptions;
  onResolve: (v: string | null) => void;
}) {
  const [value, setValue] = useState(opts.defaultValue ?? "");
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      inputRef.current?.focus();
      if (inputRef.current && "select" in inputRef.current) inputRef.current.select();
    }, 40);
    return () => clearTimeout(t);
  }, []);

  function confirm() {
    if (opts.validate) {
      const e = opts.validate(value);
      if (e) {
        setErr(e);
        return;
      }
    }
    onResolve(value);
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      onResolve(null);
    } else if (e.key === "Enter" && !opts.multiline) {
      e.preventDefault();
      confirm();
    } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && opts.multiline) {
      e.preventDefault();
      confirm();
    }
  }

  return (
    <motion.div
      initial={{ scale: 0.95, y: 10, opacity: 0 }}
      animate={{ scale: 1, y: 0, opacity: 1 }}
      exit={{ scale: 0.97, y: 6, opacity: 0 }}
      transition={{ type: "spring", damping: 24, stiffness: 300 }}
      className="glass relative w-[460px] max-w-[92vw] overflow-hidden rounded-2xl p-5 shadow-elevated"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-400/40 to-transparent" />
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-100">{opts.title}</h3>
          {opts.description && <p className="mt-1 text-xs text-slate-400">{opts.description}</p>}
        </div>
        <button
          onClick={() => onResolve(null)}
          className="rounded-lg p-1 text-slate-400 hover:bg-white/5 hover:text-slate-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {opts.multiline ? (
        <textarea
          ref={(el) => (inputRef.current = el)}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (err) setErr(null);
          }}
          onKeyDown={onKey}
          placeholder={opts.placeholder}
          className="min-h-[120px] w-full resize-y rounded-lg border border-white/10 bg-black/30 p-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-violet-500/60 focus:outline-none"
        />
      ) : (
        <input
          ref={(el) => (inputRef.current = el)}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (err) setErr(null);
          }}
          onKeyDown={onKey}
          placeholder={opts.placeholder}
          className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-violet-500/60 focus:outline-none"
        />
      )}

      {err && <p className="mt-2 text-xs text-rose-300">{err}</p>}

      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          onClick={() => onResolve(null)}
          className="rounded-lg px-3 py-1.5 text-sm text-slate-300 hover:bg-white/5"
        >
          {opts.cancelLabel ?? "Cancelar"}
        </button>
        <button
          onClick={confirm}
          className="rounded-lg bg-violet-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-violet-400"
        >
          {opts.confirmLabel ?? "OK"}
        </button>
      </div>
    </motion.div>
  );
}

function ConfirmBody({
  opts,
  onResolve,
}: {
  opts: import("../../stores/dialogStore").ConfirmOptions;
  onResolve: (v: boolean) => void;
}) {
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const t = setTimeout(() => btnRef.current?.focus(), 40);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onResolve(false);
      if (e.key === "Enter") onResolve(true);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <motion.div
      initial={{ scale: 0.95, y: 10, opacity: 0 }}
      animate={{ scale: 1, y: 0, opacity: 1 }}
      exit={{ scale: 0.97, y: 6, opacity: 0 }}
      transition={{ type: "spring", damping: 24, stiffness: 300 }}
      className="glass relative w-[440px] max-w-[92vw] overflow-hidden rounded-2xl p-5 shadow-elevated"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={clsx(
          "pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent to-transparent",
          opts.danger ? "via-rose-400/40" : "via-violet-400/40",
        )}
      />
      <div className="flex items-start gap-3">
        <div
          className={clsx(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
            opts.danger ? "bg-rose-500/20 text-rose-300 ring-1 ring-rose-500/30" : "bg-violet-500/20 text-violet-300 ring-1 ring-violet-500/30",
          )}
        >
          <AlertTriangle className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-slate-100">{opts.title}</h3>
          {opts.description && <p className="mt-1 text-sm text-slate-400">{opts.description}</p>}
        </div>
      </div>

      <div className="mt-5 flex items-center justify-end gap-2">
        <button
          onClick={() => onResolve(false)}
          className="rounded-lg px-3 py-1.5 text-sm text-slate-300 hover:bg-white/5"
        >
          {opts.cancelLabel ?? "Cancelar"}
        </button>
        <button
          ref={btnRef}
          onClick={() => onResolve(true)}
          className={clsx(
            "rounded-lg px-3 py-1.5 text-sm font-medium text-white transition",
            opts.danger ? "bg-rose-500 hover:bg-rose-400" : "bg-violet-500 hover:bg-violet-400",
          )}
        >
          {opts.confirmLabel ?? "Confirmar"}
        </button>
      </div>
    </motion.div>
  );
}
